import { Icon, IconButton, TooltipHost } from "@fluentui/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import styled from "styled-components";
import { ColumnOptionsContext } from "./ColumnOptions";
import { FilterDropdown } from "./FilterDropdown";
import { Column } from "./types";
import { describeFilter, isColumnFiltered } from "./utils";

type RowProps = {
  column: Column;
  removable?: boolean;
  dragHandleProps?: any;
  openFilterKey: string | null;
  setOpenFilterKey: (key: string | null) => void;
};

const SelectedRow = ({
  column,
  removable,
  dragHandleProps,
  openFilterKey,
  setOpenFilterKey,
}: RowProps) => {
  const isDropdownOpen = openFilterKey === column.key;
  const refInner = useRef<HTMLDivElement>(null);
  const { onToggleColumnVisibility, strings, id, taxonomy, t } =
    useContext(ColumnOptionsContext);

  const hasFilter = !!column.filter;
  const isFiltered = column.filter && isColumnFiltered(column.filter);
  const filterText = isFiltered
    ? describeFilter(column.filter, { taxonomy, t })
    : "";

  return (
    <RowInner ref={refInner} isActive={isDropdownOpen}>
      <DragHandle
        {...(dragHandleProps || {})}
        draggable={!!dragHandleProps}
        title={strings.dragHandleTitle}
      >
        <Icon iconName="GripperDotsVertical" />
      </DragHandle>
      <LabelWrap>
        <TooltipHost
          content={column.label}
          styles={{ root: { width: "100%", overflow: "hidden" } }}
        >
          <Label>{column.label}</Label>
        </TooltipHost>
        {filterText && (
          <TooltipHost
            content={filterText}
            styles={{ root: { width: "100%", overflow: "hidden" } }}
          >
            <FilterDesc>{filterText}</FilterDesc>
          </TooltipHost>
        )}
      </LabelWrap>
      {hasFilter && (
        <TooltipHost content={isFiltered ? "Filter active" : "Add filter"}>
          <IconButton
            iconProps={{ iconName: isFiltered ? "FilterSolid" : "Filter" }}
            onClick={() =>
              setOpenFilterKey(isDropdownOpen ? null : column.key)
            }
            id={id ? `${id}-filter-icon-${column.key}` : undefined}
            styles={{
              root: { minWidth: 24, width: 24, height: 24, padding: 0 },
            }}
          />
        </TooltipHost>
      )}
      {removable ? (
        <TooltipHost content={strings.visibilityCheckboxTitle}>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            onClick={() => onToggleColumnVisibility(column.key)}
            id={id ? `${id}-remove-${column.key}` : undefined}
            styles={{
              root: { minWidth: 24, width: 24, height: 24, padding: 0 },
            }}
          />
        </TooltipHost>
      ) : (
        <PinSlot>
          <Icon iconName="Pinned" />
        </PinSlot>
      )}
      {isDropdownOpen && (
        <FilterDropdown
          column={column}
          isOpen={isDropdownOpen}
          close={() => setOpenFilterKey(null)}
          target={refInner}
        />
      )}
    </RowInner>
  );
};

export const SelectedColumnsPane = () => {
  const { columns, onReorderColumn, t, id } = useContext(ColumnOptionsContext);

  // Only one filter dropdown may be open at a time.
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const [portalNode] = useState<HTMLElement>(() =>
    document.createElement("div")
  );
  useEffect(() => {
    portalNode.style.position = "fixed";
    portalNode.style.top = "0";
    portalNode.style.left = "0";
    portalNode.style.zIndex = "10000000";
    portalNode.style.pointerEvents = "none";
    document.body.appendChild(portalNode);
    return () => {
      document.body.removeChild(portalNode);
    };
  }, [portalNode]);

  const present = columns.filter((c) => c.isPresentInColumnOptions !== false);
  const fixedSelected = present.filter((c) => c.isFixed && c.isVisible);
  const draggableSelected = present.filter((c) => !c.isFixed && c.isVisible);
  const total = fixedSelected.length + draggableSelected.length;

  const handleDragEnd = (res: DropResult) => {
    if (!res.destination) return;
    const fromKey = draggableSelected[res.source.index]?.key;
    const toKey = draggableSelected[res.destination.index]?.key;
    if (fromKey === undefined || toKey === undefined) return;
    const from = columns.findIndex((c) => c.key === fromKey);
    const to = columns.findIndex((c) => c.key === toKey);
    if (from === -1 || to === -1) return;
    onReorderColumn(from, to);
  };

  return (
    <Wrapper>
      <Header>
        {t("greco.selectedColumns", "Selected")} ({total})
      </Header>
      <DragDropContext onDragEnd={handleDragEnd}>
        <ScrollArea>
          {fixedSelected.map((col) => (
            <RowOuter key={col.key}>
              <SelectedRow
                column={col}
                removable={false}
                openFilterKey={openFilterKey}
                setOpenFilterKey={setOpenFilterKey}
              />
            </RowOuter>
          ))}
          <Droppable droppableId="selected-columns">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {draggableSelected.map((col, index) => (
                  <Draggable
                    key={col.key}
                    draggableId={col.key}
                    index={index}
                  >
                    {(prov, snapshot) => {
                      if (snapshot.isDragging) {
                        return createPortal(
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            style={{
                              ...prov.draggableProps.style,
                              boxSizing: "border-box",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 8px",
                              background: "#ffffff",
                              color: "#323130",
                              font: "13px 'Segoe UI', sans-serif",
                              border: "1px solid #edebe9",
                              borderRadius: 4,
                              boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
                            }}
                          >
                            <span
                              aria-hidden={true}
                              style={{
                                color: "#605e5c",
                                fontSize: 14,
                                lineHeight: 1,
                              }}
                            >
                              {"⋮⋮"}
                            </span>
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {col.label}
                            </span>
                          </div>,
                          portalNode
                        );
                      }
                      return (
                        <RowOuter
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                        >
                          <SelectedRow
                            column={col}
                            removable
                            dragHandleProps={prov.dragHandleProps}
                            openFilterKey={openFilterKey}
                            setOpenFilterKey={setOpenFilterKey}
                          />
                        </RowOuter>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {total === 0 && (
            <Empty>{"No columns selected"}</Empty>
          )}
        </ScrollArea>
      </DragDropContext>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 13px;
  padding: 0 8px 8px 8px;
  color: ${(p) => p.theme.palette.neutralPrimary};
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const RowOuter = styled.div`
  margin-bottom: 2px;
`;

const RowInner = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  border-radius: 4px;
  background: ${(p) =>
    p.isActive ? p.theme.palette.neutralLighter : "transparent"};
  &:hover {
    background: ${(p) => p.theme.palette.neutralLighter};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  cursor: ${(p) => (p.draggable ? "grab" : "default")};
  color: ${(p) =>
    p.draggable ? p.theme.palette.neutralSecondary : p.theme.palette.neutralTertiary};
  i {
    font-size: 14px;
  }
`;

const LabelWrap = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FilterDesc = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.3;
  margin-top: 3px;
  padding: 0 4px;
  width: 100%;
  color: ${(p) => p.theme.palette.themePrimary};
`;

const Label = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  padding: 0 4px;
  width: 100%;
`;

const PinSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  color: ${(p) => p.theme.palette.neutralTertiary};
  i {
    font-size: 12px;
  }
`;

const Empty = styled.div`
  padding: 16px 8px;
  color: ${(p) => p.theme.palette.neutralSecondary};
  font-style: italic;
`;
